import axios, { AxiosRequestConfig } from 'axios'
import {
  ApiInfo,
  AuthenticationToken,
  Credentials,
  Product,
  EditorialProject,
  ProjectProductsResult,
  User,
  WorkModule
} from '@/models'
import { ApiResponse } from './api-response'
import store from '@/store'
import router from '@/router'
import { ActionTypes } from '@/store/action-types'
import { MutationTypes } from '@/store/mutation-types'
import ApiGtag, { Category } from '@/services/api-gtag'

// Shows a copyable error dialog instead of a native alert()
function showErrorDialog(title: string, message: string) {
  store.commit(MutationTypes.SET_ERROR_MODAL, {
    show: true,
    title,
    message: String(message),
    color: 'danger',
    copyable: true
  })
}

// Shows a success dialog (green, no copy button)
function showSuccessDialog(title: string, message = '') {
  store.commit(MutationTypes.SET_ERROR_MODAL, {
    show: true,
    title,
    message: String(message),
    color: 'success',
    copyable: false
  })
}

// Add a response interceptor
axios.interceptors.response.use(
  (response) => {
    return Promise.resolve(response)
  },
  function(error) {
    // Unauthorized - No auth token header:
    if (error.response && error.response.status === 401) {
      if (router.currentRoute.name !== 'Login') {
        store.dispatch(ActionTypes.AUTH_LOGOUT)
      }
    }
    return Promise.reject(error)
  }
)

export default class ApiClient {
  private static url = process.env.VUE_APP_KLDX_API_URL
  private static apiInfoInitialized = false
  private static apiInfo: ApiInfo = {
    version: '--',
    commitHash: '--'
  }

  private static requestConfig(): AxiosRequestConfig {
    const token = store.state.token
    return {
      headers: {
        Authorization: `${token.tokenType} ${token.accessToken}`
      }
    }
  }

  private static requestBlobConfig(): AxiosRequestConfig {
    const token = store.state.token
    return {
      responseType: 'blob',
      headers: {
        Authorization: `${token.tokenType} ${token.accessToken}`
      }
    }
  }

  static async getInfo(): Promise<ApiInfo> {
    if (!this.apiInfoInitialized) {
      const resp = await axios.get(`${this.url}/api/info`)
      const respData: ApiResponse = resp.data
      this.apiInfo = respData.data as ApiInfo
      this.apiInfoInitialized = true
    }

    return this.apiInfo
  }

  static async login(credentials: Credentials): Promise<AuthenticationToken> {
    const resp = await axios
      .post(`${this.url}/auth/login`, credentials)
      .catch((err) => {
        return Promise.reject(err)
      })
    const respData: ApiResponse = resp.data
    return respData.data as AuthenticationToken
  }

  static async logout(): Promise<any> {
    try {
      return await axios.get(`${this.url}/auth/logout`, this.requestConfig())
    } catch (err) {
      return err
    }
  }

  static async getUserInfo(): Promise<User> {
    const resp = await axios.get(`${this.url}/users/me`, this.requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as User
  }

  static async getEditorialProjects(): Promise<EditorialProject[]> {
    const resp = await axios.get(`${this.url}/editorial-projects`, this.requestConfig())
    const respData: ApiResponse = resp.data
    const projectsList = []
    const data = respData.data as []
    for (let i = 0; i < data.length; i++) {
      projectsList.push(data[i] as EditorialProject)
    }
    return projectsList
  }

  static async getEditorialProject(projectId: string): Promise<EditorialProject> {
    if (!projectId) {
      return Promise.reject(
        new Error(
          'No se ha recibido el dato del proyecto sobre el que buscar los Cursos Digitales'
        )
      )
    }
    const resp = await axios.get(
      `${this.url}/editorial-projects/${projectId}`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as EditorialProject
  }

  static async getEditorialProjectProducts(projectId: string, limit = 10, offset = 0): Promise<ProjectProductsResult> {
    if (!projectId) {
      return Promise.reject(
        new Error(
          'No se ha recibido el dato del proyecto sobre el que buscar los Cursos Digitales'
        )
      )
    }
    const resp = await axios.get(
      `${this.url}/editorial-projects/${projectId}/products?limit=${limit}&offset=${offset}`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as ProjectProductsResult
  }

  static async getProduct(productId: string): Promise<Product> {
    if (!productId) {
      return Promise.reject(
        new Error(
          'No se ha recibido el dato del producto sobre el que buscar LSEC'
        )
      )
    }
    const resp = await axios.get(
      `${this.url}/products/${productId}`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Product
  }

  static async getProducts(searchTxt?: string): Promise<Product[]> {
    const resp = await axios.get(
      `${this.url}/products?q=${searchTxt ?? ''}&limit=50`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    const productsList = []
    const data = respData.data as []
    for (let i = 0; i < data.length; i++) {
      productsList.push(data[i] as Product)
    }
    return productsList
  }

  static async getRelatedProducts(productId: string): Promise<Product[]> {
    const resp = await axios.get(
      `${this.url}/products/${productId}/related-materials`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    const productsList = []
    const data = respData.data as []
    for (let i = 0; i < data.length; i++) {
      productsList.push(data[i] as Product)
    }
    return productsList
  }

  static async getWorkModuleAssets(productId: string, workModuleCode: string): Promise<any> {
    if (!productId || !workModuleCode) {
      return Promise.reject(
        new Error(
          'No se ha recibido el dato del producto o workModule sobre el que buscar Assets'
        )
      )
    }
    const resp = await axios.get(
      `${this.url}/products/${productId}/${workModuleCode}/assets`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data
  }

  static async downloadFileAsset(productId: string, workModuleCode: string, assetName: string): Promise<any> {
    if (!productId || !workModuleCode || !assetName) {
      return Promise.reject(
        new Error(
          'No se ha recibido los datos del asset a descargar'
        )
      )
    }
    const resp = await axios.get(
      `${this.url}/products/${productId}/${workModuleCode}/assets/${assetName}/download`,
      this.requestBlobConfig()
    )
    const fileURL = window.URL.createObjectURL(new Blob([resp.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute('download', assetName)
    document.body.appendChild(fileLink)
    fileLink.click()
  }

  static async getWorksheetsItems(
    productId: string
  ): Promise<Record<string, any>> {
    if (!productId) {
      return Promise.reject(
        new Error(
          'No se ha recibido el dato del producto sobre el que buscar los elementos multimedia'
        )
      )
    }
    const resp = await axios.get(
      `${this.url}/products/${productId}/multimedia-elements`,
      this.requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data
  }

  static getPreviewUrl(productId: string, workModuleCode: string): Promise<string> {
    return Promise.resolve(
      `${this.url}/products/${productId}/${workModuleCode}/preview`
    )
  }

  static async downloadWorkModule(product: Product, workModule: WorkModule): Promise<void> {
    const token = store.state.token
    return await axios
      .get(`${this.url}/products/${product.id}/${workModule.code}/export`, {
        headers: {
          Accept: 'application/json',
          Authorization: `${token.tokenType} ${token.accessToken}`
        },
        validateStatus: (s) => s <= 500,
        responseType: 'blob'
      })
      .then(async(res) => {
        if (res.status !== 200) {
          const message = JSON.parse(await res.data.text()).message
          showErrorDialog('El módulo contiene errores', message)
        } else {
          const downloadUrl = window.URL.createObjectURL(new Blob([await res.data]))
          const link = document.createElement('a')
          link.href = downloadUrl
          link.setAttribute('download', `${product.id}-${workModule.code}.zip`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          ApiGtag.sendEvent(
            Category.Course,
            'download unit',
            `${product.editorialProject}/${product.id}/${workModule.code}`
          )
        }
      })
      .catch((error) => {
        showErrorDialog('El módulo contiene errores', error)
        ApiGtag.sendEvent(
          Category.Course,
          'error download unit',
          `${product.editorialProject}/${product.id}/${workModule.code}`
        )
      })
  }

  static async sendWorkModule(product: Product, workModule: WorkModule, upload: boolean): Promise<void> {
    const token = store.state.token
    return await axios
      .get(`${this.url}/products/${product.id}/${workModule.code}/send?tenant=${product.country}&upload=${upload.toString()}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `${token.tokenType} ${token.accessToken}`
        },
        // Tratamos como respuesta válida (resuelve en .then) cualquier estado
        // hasta 502, para poder leer el detalle del error en res.data.message.
        // Los estados 503+ (servidor caído/inalcanzable) rechazan y van al .catch().
        validateStatus: (s) => s <= 502,
        responseType: 'json'
      })
      .then(async(res) => {
        // 2xx (200/201): el módulo se envió / revisó correctamente.
        if (res.status <= 201) {
          if (upload) {
            showSuccessDialog('Módulo Enviado')
          } else {
            showSuccessDialog('Módulo revisado: OK')
          }
          ApiGtag.sendEvent(
            Category.Course,
            'send unit',
            `${product.editorialProject}/${product.id}/${workModule.code}`
          )
        // 502: B|Toven rechaza el módulo porque contiene errores de contenido;
        // res.data.message trae el listado ("Encontrados..."). Es el tope que
        // deja pasar validateStatus, por eso basta con comparar === 502.
        } else if (res.status === 502) {
          showErrorDialog('El módulo contiene errores', res.data.message)
          ApiGtag.sendEvent(
            Category.Course,
            'error sending unit to Btoven',
            `${product.editorialProject}/${product.id}/${workModule.code}`
          )
        // 202-501: cualquier otro error devuelto por la API al enviar/revisar.
        } else {
          if (upload) {
            showErrorDialog('Error al enviar el módulo a Btoven', res.data.message)
          } else {
            showErrorDialog('Error al revisar el módulo', res.data.message)
          }
          ApiGtag.sendEvent(
            Category.Course,
            'error sending unit to Btoven',
            `${product.editorialProject}/${product.id}/${workModule.code}`
          )
        }
      })
      .catch((error) => {
        if (upload) {
          showErrorDialog('Error al enviar el módulo a Btoven', error)
        } else {
          showErrorDialog('Error al revisar el módulo', error)
        }
        ApiGtag.sendEvent(
          Category.Course,
          'error sending unit to Btoven',
          `${product.editorialProject}/${product.id}/${workModule.code}`
        )
      })
  }

  static async createDirectLdx(product: Product, workModule: WorkModule): Promise<void> {
    const token = store.state.token
    return await axios
      .get(`${this.url}/converter/${product.id}/${workModule.code}/direct-import`, {
        headers: {
          Accept: 'application/json',
          Authorization: `${token.tokenType} ${token.accessToken}`
        },
        responseType: 'json'
      })
      .then(() => {
        showSuccessDialog('Módulo LDX generado')
        ApiGtag.sendEvent(
          Category.Course,
          'unit created',
          `${product.editorialProject}/${product.id}/${workModule.code}`
        )
      })
      .catch((error) => {
        showErrorDialog('Error al generar el módulo a LDX', error)
        ApiGtag.sendEvent(
          Category.Course,
          'error creating unit to LDX',
          `${product.editorialProject}/${product.id}/${workModule.code}`
        )
      })
  }
}
